import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { ICredential } from "./../shared/models/credential";
import { CredentialVerification } from "./shared/models/products/credential-verification";
import { OtherProduct } from "./shared/models/products/other-product";
import { IProduct } from "./shared/models/products/product";
import { PurchaseService } from "./shared/services/purchase.service";

import { RegexPatterns } from "./../shared/regex/regex-patterns";
import { NotificationsService } from "./../shared/services/notifications.service";

import { PurchaseStateService } from "./shared/services/purchase-state.service";
import { ViewStateService } from "./../shared/services/view-state.service";
import { UserService } from "./../shared/services/user.service";
import { CommonApiService } from "./../shared/services/api/common-api.service";
import { Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-purchase",
  templateUrl: "./purchase.component.html",
  styleUrls: ["./purchase.component.css"],
})
export class PurchaseComponent implements OnInit, OnDestroy {
  personId: string;
  isTaxableUser: boolean;
  productsForPurchase: IProduct[];
  credentialsHeld: ICredential[];
  freeServices: any[];
  revokedCRT: boolean;
  isRRT: boolean;
  isRecertification: any;
  showPinWarning = false;
  showCertificateWarning = false;
  showCardWarning = false;
  private destroy$ = new Subject<void>();

  private get isCredentialVerificationRequest(): boolean {
    const routeData = this.route.snapshot.data[0];
    if (routeData && routeData["cvPurchase"]) {return true;}
    return false;
  }

  constructor(
    private purchaseService: PurchaseService,
    private viewState: ViewStateService,
    private userService: UserService,
    private common: CommonApiService,
    private route: ActivatedRoute,
    private router: Router,
    private notifications: NotificationsService,
    private purchaseState: PurchaseStateService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    this.isRecertification =
      this.router.routerState.snapshot.root.queryParams["recert"];
    this.personId = this.userService.CurrentUser.personId;
    this.revokedCRT = this.userService.CurrentUser.revokedCRT;
    this.getProductData();
  }

  limitInputLength(event: Event, maxLength: number) {
    const input = event.target as HTMLInputElement;

    if (input.value === "") {
      return;
    }

    const value = parseInt(input.value, 10);

    if (isNaN(value) || value <= 0) {
      input.value = "";
      return;
    }

    if (input.value.length > maxLength) {
      input.value = input.value.slice(0, maxLength);
    }

    if (value > 10) {
      input.value = "10";
    }
  }

  setTempProductAndNavigate(product: IProduct) {
    this.purchaseState.setTempProductData(product);

    if (product.constructor == CredentialVerification)
      {this.router.navigate(["purchase/credential-verification/options"]);}
    else {this.router.navigate(["purchase/products/options"]);}
  }

  private getProductData() {
    this.purchaseService
      .getProfile(this.personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.isTaxableUser = this.determineTaxableState(profile.stateProvince);

        this.purchaseService
          .getCredentials(this.personId)
          .pipe(takeUntil(this.destroy$))
          .subscribe((res: ICredential[]) => {
            this.credentialsHeld = res;
            this.isRRT =
              this.credentialsHeld.findIndex((p) => p.credential == "RRT") > -1;
            if (this.revokedCRT) {
              const i = this.credentialsHeld.findIndex(
                (p) => p.credential == "CRT"
              );
              this.credentialsHeld.splice(i, 1);
            }
            this.getFreeServices();
          });
      });
  }

  isPin(product): boolean {
    return product.isPin;
  }

  isCertificate(product): boolean {
    if (product.title.indexOf("Certif") > -1) {return true;}
    else {return false;}
  }

  isWalletCard(product): boolean {
    if (product.title.indexOf("Card") > -1) {return true;}
    else {return false;}
  }

  isExpired(): boolean {
    if (
      this.userService.CurrentUser.hasExpiredCredentials &&
      !this.userService.CurrentUser.credentialHolder
    ) {
      return true;
    }
    return false;
  }

  onItemChecked(productChecked, i) {
    this.productsForPurchase[i].selected = productChecked;
    //    if(productChecked && this.productsForPurchase[i].isPin)
    //      this.showPinWarning = true;
    if (this.productsForPurchase.find((p) => p.selected && p.isPin)) {
      this.showPinWarning = true;
    } else {
      this.showPinWarning = false;
    }
    if (
      this.productsForPurchase.find(
        (p) => p.selected && p.title.indexOf("Certif") > -1
      )
    ) {
      this.showCertificateWarning = true;
    } else {
      this.showCertificateWarning = false;
    }
    if (
      this.productsForPurchase.find(
        (p) => p.selected && p.title.indexOf("Card") > -1
      )
    ) {
      this.showCardWarning = true;
    } else {
      this.showCardWarning = false;
    }
  }

  setInitial(initial, i) {
    this.productsForPurchase[i].initials = initial;
  }

  setQuantity(qty, i) {
    this.productsForPurchase[i].quantity = qty;
  }

  addToCartAndPay() {
    if (this.productsForPurchase.find((p) => p.selected)) {
      if (this.validateForm()) {
        this.productsForPurchase.forEach((product) => {
          if (product.selected) {this.purchaseState.patchProducts(product);}
        });
        this.router.navigate(["purchase/checkout"]);
      }
    } else {
      this.notifications.error(
        "No products selected",
        "Please select at least one product to proceed."
      );
    }
  }

  validateForm() {
    let retVal = true;
    this.productsForPurchase.forEach((product) => {
      if (
        product.isPin &&
        product.selected &&
        (!product.initials || product.initials == "")
      ) {
        this.notifications.error(
          "Pin Initials Required",
          "Please enter initials to engrave on PIN.  Initials are all alpha."
        );
        retVal = false;
      }
      if (
        product.initials &&
        product.selected &&
        !product.initials.match(RegexPatterns.lettersOnly)
      ) {
        this.notifications.error(
          "Pin Initials Invalid",
          "Please make sure PIN initials are all alpha."
        );
        retVal = false;
      }
    });
    return retVal;
  }

  private determineTaxableState(stateProvince: string) {
    return stateProvince == "KS" || stateProvince == "Kansas";
  }

  private getFreeServices() {
    this.common
      .getFreeServices(this.personId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (result) => {
          this.freeServices = result;
          this.getAvailableItemsForPurchase();
        },
        (error) => {
          console.error("Unable to search free services", error);
        }
      );
  }

  private getAvailableItemsForPurchase() {
    const currentDate = new Date();
    this.common
      .getServiceCodesByCredentials(
        this.credentialsHeld
          .filter(
            (f) =>
              f.expirationDate == null ||
              new Date(f.expirationDate) >= currentDate
          )
          .map((c) => c.credential)
          .join(",")
      )
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          const products = [];
          const items = response.sort(
            (a, b) => a.productSortGroup - b.productSortGroup
          );

          items.forEach((item) => {
            if (this.isExpired() && item.code !== "L01") {
              return;
            }

            const product =
              item.code === "L01"
                ? new CredentialVerification()
                : new OtherProduct();
            product.activePrice = item.activeRate;
            product["effectivePrice"] =
              this.purchaseState.getEffectiveProductPrice(item);
            product.inactivePrice = item.inactiveRate;
            product.serviceCodeId = item.id;
            product.code = item.code;
            product.title = item.codeDescription;
            product.quantity = 1;
            product.taxable = item.taxable && this.isTaxableUser;
            product.isPin = item.askPin;
            product.image = this.getImageForProduct(item.code);

            if (
              this.freeServices.findIndex((p) => p.serviceCodeId == item.id) >=
                0 &&
              this.purchaseState.currentState.products.findIndex(
                (p) => p.serviceCodeId == item.id && p.activePrice == 0
              ) < 0
            ) {
              product.activePrice = 0;
              product.inactivePrice = 0;
              product["effectivePrice"] = 0;
            }
            products.push(product);
          });

          return products;
        })
      )
      .subscribe(
        (products: IProduct[]) => {
          this.productsForPurchase = products;
          this.viewState.stopLoading();

          if (this.isCredentialVerificationRequest) {
            this.setTempProductAndNavigate(
              products.find((p) => p.code == "L01")
            );
          } else {
            products.splice(
              products.findIndex((p) => p.code == "L01"),
              1
            );
          }
        },
        (error) => {
          console.error("Error fetching products for purchase", error);
          this.viewState.stopLoading();
        }
      );
  }

  private getImageForProduct(code: string) {
    if (code == "C01") {return "/assets/images/pins/crt-pin.png";}
    else if (code == "R01") {return "/assets/images/pins/rrt-pin.png";}
    else if (code == "A01") {return "/assets/images/pins/rrt-accs-pin.png";}
    else if (code == "D01" && this.isRRT)
      {return "/assets/images/pins/rrt-sds-pin.png";}
    else if (code == "D01" && !this.isRRT)
      {return "/assets/images/pins/crt-sds-pin.png";}
    else if (code == "P01") {return "/assets/images/pins/cpft-pin.png";}
    else if (code == "S01" && this.isRRT)
      {return "/assets/images/pins/rrt-nps-pin.png";}
    else if (code == "S01" && !this.isRRT)
      {return "/assets/images/pins/crt-nps-pin.png";}
    else if (code == "X01") {return "/assets/images/pins/rpft-pin.png";}
    else if (code == "AE01") {return "/assets/images/pins/ae-c-pin.png";}
    else {return "";}
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
